import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Phone, Shield, Calendar, MoreVertical, UserCheck, UserX, Key } from 'lucide-react';
import { UserData } from '../services/userService';
import ResetPasswordModal from './ResetPasswordModal';

interface UserCardProps {
  user: UserData;
  currentUserId: string;
  onToggleStatus: (userId: string) => Promise<void>;
  onPasswordReset?: () => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, currentUserId, onToggleStatus, onPasswordReset }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const isCurrentUser = user.id === currentUserId;
  const isAdmin = user.user_type === 'admin';

  // Close modals when user data changes (e.g., after status toggle)
  useEffect(() => {
    setShowMenu(false);
    setShowConfirmDialog(false);
    setShowResetPasswordModal(false);
  }, [user.is_active, user.id]);

  const handleToggleStatus = async () => {
    if (isCurrentUser) {
      return; // Prevent self-deactivation
    }

    setLoading(true);
    try {
      // Close modal immediately before making the API call
      setShowConfirmDialog(false);
      setShowMenu(false);
      
      await onToggleStatus(user.id);
    } catch (error) {
      console.error('Error toggling user status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = () => {
    setShowResetPasswordModal(false);
    setShowMenu(false);
    if (onPasswordReset) {
      onPasswordReset();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = () => {
    if (!user.is_active) return 'text-red-600 bg-red-50';
    if (isAdmin) return 'text-purple-600 bg-purple-50';
    return 'text-green-600 bg-green-50';
  };

  const getStatusText = () => {
    if (!user.is_active) return 'Inactive';
    if (isAdmin) return 'Admin';
    return 'Active User';
  };

  return (
    <div className={`bg-white rounded-lg border ${!user.is_active ? 'border-red-200 opacity-75' : 'border-gray-200'} p-4 hover:shadow-md transition-shadow relative`}>
      {/* Header with Menu */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAdmin ? 'bg-purple-100' : 'bg-primary-100'}`}>
            <User className={`w-5 h-5 ${isAdmin ? 'text-purple-600' : 'text-primary-600'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
              <span>{user.phone}</span>
              {isCurrentUser && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">You</span>
              )}
            </h3>
            <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
              <Shield className="w-3 h-3" />
              <span>{getStatusText()}</span>
            </div>
          </div>
        </div>

        {/* Actions Menu */}
        {!isCurrentUser && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              <MoreVertical className="w-4 h-4 text-gray-500" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[100] min-w-[140px]">
                <button
                  onClick={() => {
                    setShowResetPasswordModal(true);
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 text-orange-600"
                  disabled={loading}
                >
                  <Key className="w-4 h-4" />
                  <span>Reset Password</span>
                </button>
                
                <div className="border-t border-gray-100 my-1"></div>
                
                <button
                  onClick={() => {
                    setShowConfirmDialog(true);
                    setShowMenu(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                    user.is_active ? 'text-red-600' : 'text-green-600'
                  }`}
                  disabled={loading}
                >
                  {user.is_active ? (
                    <>
                      <UserX className="w-4 h-4" />
                      <span>Disable</span>
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4" />
                      <span>Enable</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Details */}
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex items-center space-x-2">
          <Phone className="w-4 h-4" />
          <span>Phone: {user.phone}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4" />
          <span>Created: {formatDate(user.created_at)}</span>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw',
            height: '100vh',
            zIndex: 2147483647,
            isolation: 'isolate',
            pointerEvents: 'auto',
            transform: 'translateZ(0)',
            contain: 'layout style paint',
            willChange: 'transform'
          }}
        >
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative" style={{ zIndex: 100000 }}>
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                user.is_active ? 'bg-red-100' : 'bg-green-100'
              }`}>
                {user.is_active ? (
                  <UserX className="w-5 h-5 text-red-600" />
                ) : (
                  <UserCheck className="w-5 h-5 text-green-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {user.is_active ? 'Disable User' : 'Enable User'}
                </h3>
                <p className="text-sm text-gray-600">
                  {user.is_active 
                    ? 'This user will no longer be able to access the system'
                    : 'This user will be able to access the system again'
                  }
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-700">
                <strong>User:</strong> {user.phone} ({getStatusText()})
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  user.is_active
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    {user.is_active ? (
                      <>
                        <UserX className="w-4 h-4" />
                        <span>Disable User</span>
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-4 h-4" />
                        <span>Enable User</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        onPasswordReset={handlePasswordReset}
        targetUser={user}
      />

      {/* Click outside to close menu */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
};

export default UserCard;
